import { Box, Card, CardContent, Link, useTheme } from '@mui/material'
import { styled } from '@mui/material/styles'
import React, { useCallback } from 'react'
import { UseFormReturn } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { IResource } from 'src/generated-types/resource'
import { Flex } from 'src/styles/flexComponent'
import { AppRoutesNavigation } from '../../app-routes'
import { useNavigation } from '../../context/NavigationContext'
import { DynamicTextTypography } from '../DynamicTextTypography'
import BulkAssignVessels from '../locality/BulkAssignVessels'
import { TaskCalendarEvent } from '../locality/VesselTasksCalendarDialog'
import { EmptyStatusCard } from '../mooring/EmptyStatusCard'
import { FlexSpinner } from '../Spinner'
import { coreUtils } from '../../utils'

interface VesselSidebarProps {
  resources?: { items: IResource[] }
  selectedResourceIdSet: Set<number>
  form: UseFormReturn<any>
  taskFilterChange: (params: { resourceIds?: Array<{ id: number }>; localityIds?: Array<{ id: number }> }) => void
  vesselId?: number
  customerId: number
  pop?: (data?: any) => void
  isLoading?: boolean
  selectedTasks: TaskCalendarEvent[]
  setSelectedTasks: (tasks: TaskCalendarEvent[]) => void
}

const VesselSidebar: React.FC<VesselSidebarProps> = ({
  resources,
  selectedResourceIdSet,
  form,
  taskFilterChange,
  vesselId,
  customerId,
  pop,
  selectedTasks,
  isLoading,
  setSelectedTasks,
}) => {
  const { t } = useTranslation(['common'])
  const theme = useTheme()
  const { navigateTo } = useNavigation()

  const handleVesselCardClick = useCallback(
    (resource: IResource, event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
      const currentResourceIds = form.getValues('resourceIds') || []
      const allResources = resources?.items || []

      const newResourceIds = coreUtils.handleRowSelectFunc(
        currentResourceIds,
        { id: resource.id },
        allResources.map(r => ({ id: r.id })),
        event,
      )

      form.setValue('resourceIds', newResourceIds)
      const formValues = form.getValues()
      taskFilterChange({
        resourceIds: vesselId ? [{ id: vesselId }] : newResourceIds,
        localityIds: formValues.localityIds || undefined,
      })
    },
    [form, taskFilterChange, vesselId, resources],
  )

  if (isLoading) {
    return <FlexSpinner size="small" />
  }

  return (
    <VesselSidebarContainer>
      <Flex.Row justifyContent="space-between" alignItems="center">
        <DynamicTextTypography
          text={t('resources', { ns: 'task' })}
          variant="h6"
          fontWeight={600}
          sx={{ marginBottom: 2 }}
        />
        <BulkAssignVessels
          selectedTasks={selectedTasks}
          customerId={customerId}
          onAssignCallback={() => {
            setSelectedTasks([])
          }}
        />
      </Flex.Row>
      <ScrollableVesselContainer>
        {!resources?.items || resources.items.length == 0 ? (
          <EmptyStateContainer>
            <EmptyStatusCard
              imageWidth="132px"
              header={t('no-vessels-available', { ns: 'common' })}
              content={t('no-vessels-available-description', { ns: 'common' })}
              headerFontSize="18px"
              contentFontSize="14px"
              contentTopMargin="10px"
              imageContainerClasses="pb-4"
              contentClasses="mt-2"
            />
            <Link
              component="button"
              variant="body2"
              onClick={() => {
                navigateTo(AppRoutesNavigation.Customer.vessels(customerId))
                pop?.()
              }}
              sx={{
                color: theme.palette.primary.main,
                textDecoration: 'underline',
                cursor: 'pointer',
                marginTop: 2,
                '&:hover': {
                  color: theme.palette.primary.dark,
                },
              }}
            >
              {t('go-to-vessels-page', { ns: 'common' })}
            </Link>
          </EmptyStateContainer>
        ) : (
          resources.items.map((resource: IResource) => {
            const isSelected = selectedResourceIdSet.has(resource.id)
            return (
              <VesselCard key={resource.id} onClick={e => handleVesselCardClick(resource, e)} isSelected={isSelected}>
                <CardContent sx={{ padding: '6px', paddingTop: '10px' }}>
                  <DynamicTextTypography
                    text={resource.name}
                    variant="body1"
                    fontWeight={isSelected ? 600 : 400}
                    textAlign={'left'}
                    color={isSelected ? theme.palette.primary.main : theme.palette.text.primary}
                    maxLength={25}
                  />
                </CardContent>
              </VesselCard>
            )
          })
        )}
      </ScrollableVesselContainer>
    </VesselSidebarContainer>
  )
}

// Styled Components
const VesselSidebarContainer = styled(Box)(({ theme }) => ({
  minWidth: '250px',
  maxWidth: '250px',
  borderRight: `1px solid ${theme.palette.divider}`,
  paddingRight: theme.spacing(2),
}))

const ScrollableVesselContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
  overflowY: 'auto',
  overflowX: 'hidden',
  paddingRight: '8px',
  maxHeight: 'calc(100vh - 400px)',
  '&::-webkit-scrollbar': {
    width: '8px',
  },
  '&::-webkit-scrollbar-track': {
    backgroundColor: theme.palette.grey[200],
    borderRadius: '4px',
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: theme.palette.grey[400],
    borderRadius: '4px',
    '&:hover': {
      backgroundColor: theme.palette.grey[500],
    },
  },
}))

const EmptyStateContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(3),
  textAlign: 'center',
  width: '100%',
}))

interface VesselCardProps {
  isSelected?: boolean
}

const VesselCard = styled(Card)<VesselCardProps>(({ theme, isSelected }) => ({
  cursor: 'pointer',
  width: '100%',
  border: `2px solid ${isSelected ? theme.palette.primary.main : theme.palette.divider}`,
  backgroundColor: isSelected ? `${theme.palette.primary.light}20` : theme.palette.background.paper,
  transition: 'all 0.2s ease-in-out',
  height: '45px',
  '&:hover': {
    borderColor: theme.palette.primary.main,
    transform: 'translateX(2px)',
    boxShadow: theme.shadows[4],
  },
  minHeight: '45px',
}))

export default VesselSidebar
